// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import keys from 'lodash/keys';
import size from 'lodash/size';
import isNull from 'lodash/isNull';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { Button, makeStyles } from '@material-ui/core';

import { getI18n } from "../../../utils/intl";

import {
  FILTER_TYPE_RANK,
  FILTER_TYPE_NAME,
} from '../../../constants/filter';
import { getSearchPlaceHolderText } from '../utils/filter';

import FilterByRank from '../FilterByRank';
import FilterByName from '../FilterByName';

import styles from './styles';

const Filter = (props) => {
  const {
    entityNameMap, title, entityType, filterInitState,
    handleFilterChange, closeFilter, maxAllowedEntities, showFilter, handleSearchFilterNameChange,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    data: [],
    filterType: FILTER_TYPE_RANK,
    selectedItems: {},
    selectedRank: 1,
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  useEffect(() => {
    if (filterInitState.filterType === FILTER_TYPE_RANK) {
      setState({
        filterType: filterInitState.filterType,
        selectedRank: filterInitState.rank,
      });
    } else {
      setState({
        filterType: filterInitState.filterType,
        selectedItems: Object.assign({}, filterInitState.selectedItems),
      });
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClick, false);
    document.addEventListener('keydown', handleKeyDown, false);

    return () => {
      document.removeEventListener('mousedown', handleClick, false);
      document.removeEventListener('keydown', handleKeyDown, false);
    }
  }, []);

  const getApplyBtnStatus = () => {
    const { filterType, selectedRank, selectedItems, isInvalidRankSize } = state;
    let selectedEntities = 0;
    if (filterType === FILTER_TYPE_RANK) {
      if (!isInvalidRankSize) {
        selectedEntities = selectedRank;
      }
    } else if (filterType === FILTER_TYPE_NAME) {
      selectedEntities = keys(selectedItems).length;
    }
    return ((selectedEntities > 0) && (selectedEntities <= maxAllowedEntities));
  }

  const handleClick = (event) => {
    if (event.which === 1) {
      const targetElement = event.target;
      const filterContainer = targetElement.closest('#filter-container');
      if (isNull(filterContainer)) {
        closeFilter();
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.keyCode === 27) {
      closeFilter();
    }
  }

  const toggleFilterType = filterType => {
    setState({ filterType });
  };

  const updateRankSize = (selectedRank, isInvalid) => {
    setState({ selectedRank, isInvalidRankSize: isInvalid });
  }

  const updateSelectedItems = selectedItems => {
    setState({ selectedItems });
  }

  const applyFilter = () => {
    const filterType = state.filterType;
    const { selectedItems } = state;
    const count = size(selectedItems);
    const filterData = {
      entityType: entityType,
      filterType,
    };
    if (filterType === FILTER_TYPE_RANK) {
      filterData.rank = state.selectedRank;
    } else if (filterType === FILTER_TYPE_NAME) {
      filterData.selectedItems = state.selectedItems;
      filterData.selectedItemsCount = count;
    }
    handleFilterChange(filterData);
  };

  const placeHolderText = getSearchPlaceHolderText(entityType, intl);
  const disableApplyBtn = !(getApplyBtnStatus());
  const filterContainer =
    state.filterType === FILTER_TYPE_NAME ? (
      <FilterByName
        filterData={entityNameMap}
        placeHolderText={placeHolderText}
        selectedItems={state.selectedItems}
        updateSelectedItems={updateSelectedItems}
        maxAllowedEntities={maxAllowedEntities}
        handleSearchFilterNameChange={handleSearchFilterNameChange}
      />
    ) : (
      <FilterByRank
        updateRankSize={updateRankSize}
        updatedFilterRankSize={state.selectedRank}
        maxAllowedEntities={maxAllowedEntities}
      />
    );
    
  return showFilter ? (
    <div
      className={classes.filter}
      id={'filter-container'}
    >
      <div className={classes.filterTitle}>{title}</div>
      <div className={classes.filterTypeContainer} >
        <span>
          <Button
            onClick={() => toggleFilterType(FILTER_TYPE_RANK)}
            className={clsx(
              classes.filterTypeBtn,
              state.filterType === FILTER_TYPE_RANK
                ? classes.filterTypeBtnSelected
                : null,
            )}
            data-apim-test={'filter-type-rank'}
          >
            Rank
          </Button>
        </span>
        <span className={classes.separatedBtn}>
          <Button
            onClick={() => toggleFilterType(FILTER_TYPE_NAME)}
            className={clsx(
              classes.filterTypeBtn,
              state.filterType === FILTER_TYPE_NAME
                ? classes.filterTypeBtnSelected
                : null,
            )}
            data-apim-test={'filter-type-name'}
          >
            Name
          </Button>
        </span>
      </div>
      {filterContainer}
      <div className={classes.buttonBar}>
        <Button
          color="secondary"
          data-apim-test={'filter-container-apply-btn'}
          onClick={applyFilter}
          variant={'contained'}
          disabled={disableApplyBtn}
        >
          {intl.getI18nMessage('label.analytics.dashboard.label.apply')}
        </Button>
        <Button
          className={classes.cancelBtn}
          data-apim-test={'filter-container-cancel-btn'}
          primary
          onClick={closeFilter}
        >
          {intl.getI18nMessage('label.analytics.dashboard.label.cancel')}
        </Button>
      </div>
    </div>
  ) : null;
}

Filter.propTypes = {
  closeFilter: PropTypes.func,
  entityNameMap: PropTypes.object,
  entityType: PropTypes.string,
  filterInitState: PropTypes.object,
  handleFilterChange: PropTypes.func,
  showFilter: PropTypes.bool,
  title: PropTypes.string,
  maxAllowedEntities: PropTypes.number,
  handleSearchFilterNameChange: PropTypes.func,
};

export default Filter;
